import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import ConventionEditor from '../ConventionEditor';
import { Plus, Send, Shield, Award, Loader2, Mail, UserPlus, Users } from 'lucide-react';
import { formatCodeNAF } from '../../utils/codeNaf';

export default function ModalsCreateEnvoi({
  openCreateModal, setOpenCreateModal,
  openEnvoiModal, setOpenEnvoiModal,
  formData, setFormData,
  envoiData, setEnvoiData,
  selectedConvention,
  coopersList, expertsList,
  showCreateRecipient, setShowCreateRecipient,
  newRecipientEmail, setNewRecipientEmail,
  newRecipientPrenom, setNewRecipientPrenom,
  newRecipientNom, setNewRecipientNom,
  newRecipientEntreprise, setNewRecipientEntreprise,
  creatingRecipient,
  handleCreateConvention,
  handleCreateRecipientByEmail,
  handleEnvoyerConvention,
}) {
  return (
    <>
      {/* Modal Création */}
      <Dialog open={openCreateModal} onOpenChange={setOpenCreateModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Créer un nouveau convention</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Type de convention *</Label>
              <Select
                value={formData.type_convention}
                onValueChange={(value) => setFormData({...formData, type_convention: value})}
              >
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cooper">
                    <div className="flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      COOPER'S
                    </div>
                  </SelectItem>
                  <SelectItem value="expert">
                    <div className="flex items-center gap-2">
                      <Award className="h-4 w-4" />
                      EXPERT
                    </div>
                  </SelectItem>
                  <SelectItem value="autre">
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      Autre Partenaire
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Champs pour type "autre" */}
            {formData.type_convention === 'autre' && (
              <Card className="bg-blue-50 border-2 border-blue-200">
                <CardHeader>
                  <CardTitle className="text-sm">Informations du Partenaire</CardTitle>
                  <CardDescription className="text-xs">
                    Renseignez les coordonnées du partenaire
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Prénom *</Label>
                      <Input
                        value={formData.destinataire_autre.prenom}
                        onChange={(e) => setFormData({
                          ...formData,
                          destinataire_autre: {...formData.destinataire_autre, prenom: e.target.value}
                        })}
                        className="mt-1"
                        required
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Nom *</Label>
                      <Input
                        value={formData.destinataire_autre.nom}
                        onChange={(e) => setFormData({
                          ...formData,
                          destinataire_autre: {...formData.destinataire_autre, nom: e.target.value}
                        })}
                        className="mt-1"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={formData.destinataire_autre.email}
                      onChange={(e) => setFormData({
                        ...formData,
                        destinataire_autre: {...formData.destinataire_autre, email: e.target.value}
                      })}
                      className="mt-1"
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-xs">Entreprise</Label>
                    <Input
                      value={formData.destinataire_autre.entreprise}
                      onChange={(e) => setFormData({
                        ...formData,
                        destinataire_autre: {...formData.destinataire_autre, entreprise: e.target.value}
                      })}
                      placeholder="Optionnel"
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">SIRET</Label>
                      <Input
                        value={formData.destinataire_autre.siret}
                        onChange={(e) => setFormData({
                          ...formData,
                          destinataire_autre: {...formData.destinataire_autre, siret: e.target.value}
                        })}
                        placeholder="Optionnel"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Code NAF</Label>
                      <Input
                        value={formData.destinataire_autre.code_naf}
                        onChange={(e) => setFormData({
                          ...formData,
                          destinataire_autre: {...formData.destinataire_autre, code_naf: e.target.value.toUpperCase()}
                        })}
                        onBlur={(e) => setFormData({
                          ...formData,
                          destinataire_autre: {...formData.destinataire_autre, code_naf: formatCodeNAF(e.target.value)}
                        })}
                        placeholder="Ex: 43.99C"
                        maxLength={6}
                        className="mt-1 font-mono"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div>
              <Label>Titre du convention *</Label>
              <Input
                value={formData.titre}
                onChange={(e) => setFormData({...formData, titre: e.target.value})}
                placeholder="Ex: Convention de partenariat COOPER'S 2025"
                className="mt-2"
              />
            </div>

            <div>
              <Label>Durée (mois) *</Label>
              <Input
                type="number"
                value={formData.duree_mois}
                onChange={(e) => setFormData({...formData, duree_mois: parseInt(e.target.value)})}
                className="mt-2"
              />
            </div>

            <div>
              <Label>Montant mensuel (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.montant_mensuel || ''}
                onChange={(e) => setFormData({...formData, montant_mensuel: parseFloat(e.target.value) || null})}
                placeholder="Optionnel"
                className="mt-2"
              />
            </div>

            <div>
              <Label className="mb-2 block font-semibold">Contenu du convention *</Label>
              <ConventionEditor
                value={formData.contenu_html}
                onChange={(html) => setFormData({...formData, contenu_html: html})}
                type={formData.type_convention}
                height="400px"
              />
            </div>

            <div>
              <Label>Conditions spécifiques</Label>
              <Textarea
                value={formData.conditions_specifiques}
                onChange={(e) => setFormData({...formData, conditions_specifiques: e.target.value})}
                rows={4}
                placeholder="Conditions particulières..."
                className="mt-2"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreateModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleCreateConvention} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Créer le convention
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Envoi */}
      <Dialog open={openEnvoiModal} onOpenChange={setOpenEnvoiModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Envoyer le convention pour signature</DialogTitle>
          </DialogHeader>

          {selectedConvention && (
            <div className="space-y-4 py-4">
              <div className="bg-blue-50 p-3 rounded">
                <p className="font-semibold">{selectedConvention.titre}</p>
                <p className="text-sm text-slate-600">{selectedConvention.numero_contrat}</p>
              </div>

              {selectedConvention.type_convention !== 'autre' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Destinataire *</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowCreateRecipient(!showCreateRecipient)}
                      className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 px-2"
                    >
                      <UserPlus className="h-4 w-4 mr-1" />
                      {showCreateRecipient ? 'Choisir existant' : 'Créer nouveau'}
                    </Button>
                  </div>

                  {!showCreateRecipient ? (
                    <Select
                      value={envoiData.destinataire_id}
                      onValueChange={(value) => setEnvoiData({...envoiData, destinataire_id: value})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Sélectionner un destinataire" />
                      </SelectTrigger>
                      <SelectContent>
                        {(selectedConvention.type_convention === 'cooper' || selectedConvention.type_contrat === 'cooper') ? (
                          coopersList.map(c => {
                            const displayName = c.entreprise || c.raison_sociale ||
                              (c.user ? `${c.user.prenom || ''} ${c.user.nom || ''}`.trim() : '') ||
                              c.user?.email || 'Cooper sans nom';
                            return (
                              <SelectItem key={c.id} value={c.id}>
                                {displayName}
                              </SelectItem>
                            );
                          })
                        ) : (
                          expertsList.map(e => {
                            const displayName = e.entreprise || e.raison_sociale ||
                              `${e.prenom || ''} ${e.nom || ''}`.trim() ||
                              e.email || 'Expert sans nom';
                            return (
                              <SelectItem key={e.id} value={e.id}>
                                {displayName}
                              </SelectItem>
                            );
                          })
                        )}
                      </SelectContent>
                    </Select>
                  ) : (
                    /* Formulaire de création rapide par email */
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                      <div className="flex items-center gap-2 text-blue-700 mb-2">
                        <Mail className="h-4 w-4" />
                        <span className="font-medium text-sm">Créer un nouveau destinataire</span>
                      </div>

                      <div>
                        <Label className="text-sm">Email *</Label>
                        <Input
                          type="email"
                          value={newRecipientEmail}
                          onChange={(e) => setNewRecipientEmail(e.target.value)}
                          placeholder="email@exemple.com"
                          className="mt-1"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-sm">Prénom</Label>
                          <Input
                            value={newRecipientPrenom}
                            onChange={(e) => setNewRecipientPrenom(e.target.value)}
                            placeholder="Prénom"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">Nom</Label>
                          <Input
                            value={newRecipientNom}
                            onChange={(e) => setNewRecipientNom(e.target.value)}
                            placeholder="Nom"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm">Entreprise</Label>
                        <Input
                          value={newRecipientEntreprise}
                          onChange={(e) => setNewRecipientEntreprise(e.target.value)}
                          placeholder="Nom de l'entreprise"
                          className="mt-1"
                        />
                      </div>

                      <p className="text-xs text-blue-600">
                        Un email d'invitation sera envoyé automatiquement.
                        Le destinataire sera créé en tant que {(selectedConvention.type_convention === 'cooper' || selectedConvention.type_contrat === 'cooper') ? "COOPER'S" : "EXPERT"}.
                      </p>

                      <Button
                        onClick={handleCreateRecipientByEmail}
                        disabled={creatingRecipient || !newRecipientEmail}
                        className="w-full bg-blue-600 hover:bg-blue-700"
                        size="sm"
                      >
                        {creatingRecipient ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Création en cours...
                          </>
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-2" />
                            Créer et sélectionner
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {(selectedConvention.type_convention === 'autre' || selectedConvention.type_contrat === 'autre') && selectedConvention.destinataire_autre && (
                <div className="bg-blue-50 p-4 rounded border border-blue-200">
                  <Label className="font-semibold mb-2 block">Destinataire</Label>
                  <div className="space-y-1 text-sm">
                    <p><strong>Nom :</strong> {selectedConvention.destinataire_autre.prenom} {selectedConvention.destinataire_autre.nom}</p>
                    <p><strong>Email :</strong> {selectedConvention.destinataire_autre.email}</p>
                    {selectedConvention.destinataire_autre.entreprise && (
                      <p><strong>Entreprise :</strong> {selectedConvention.destinataire_autre.entreprise}</p>
                    )}
                    {selectedConvention.destinataire_autre.siret && (
                      <p><strong>SIRET :</strong> {selectedConvention.destinataire_autre.siret}</p>
                    )}
                  </div>
                </div>
              )}

              <div>
                <Label>Message d'accompagnement</Label>
                <Textarea
                  value={envoiData.message_accompagnement}
                  onChange={(e) => setEnvoiData({...envoiData, message_accompagnement: e.target.value})}
                  rows={4}
                  placeholder="Message qui accompagnera le convention..."
                  className="mt-2"
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenEnvoiModal(false)}>
              Annuler
            </Button>
            <Button onClick={handleEnvoyerConvention} className="bg-blue-600 hover:bg-blue-700">
              <Send className="h-4 w-4 mr-2" />
              Envoyer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
